import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { motion } from 'framer-motion';
import { Heart, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ThankYouNodeData } from '../utils/funnelTypes';

function ThankYouNodeComponent({ data, selected }: NodeProps) {
  const { t } = useTranslation('funnel');
  const nodeData = data as unknown as ThankYouNodeData;

  const truncateUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return (
        parsed.hostname +
        (parsed.pathname.length > 18
          ? parsed.pathname.slice(0, 18) + '…'
          : parsed.pathname)
      );
    } catch {
      return url.length > 32 ? url.slice(0, 32) + '…' : url;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.3, ease: 'easeOut' }}
      className={`
        relative rounded-xl border bg-[#18181B]/90 backdrop-blur-md shadow-lg
        min-w-[200px] max-w-[250px] overflow-hidden transition-all duration-200
        ${selected
          ? 'border-violet-500 shadow-violet-500/20'
          : 'border-violet-500/25 hover:border-violet-500/50'
        }
      `}
    >
      {/* top accent bar */}
      <div className="h-0.5 w-full bg-gradient-to-r from-violet-500/60 via-violet-400 to-violet-500/60" />

      <div className="p-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-500/15 ring-1 ring-violet-500/30 flex-shrink-0">
            <Heart className="h-4 w-4 text-violet-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-medium uppercase tracking-wider text-violet-400/70">
              {t('thank_you_label', 'Thank You')}
            </p>
            <p className="text-xs font-semibold text-violet-300 truncate">
              {nodeData.label}
            </p>
            {nodeData.url && (
              <a
                href={nodeData.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1 mt-0.5 group/link"
                title={nodeData.url}
              >
                <ExternalLink className="h-2.5 w-2.5 text-violet-500/60 group-hover/link:text-violet-400 transition-colors flex-shrink-0" />
                <span className="text-[9px] text-zinc-500 group-hover/link:text-violet-400 transition-colors truncate max-w-[150px]">
                  {truncateUrl(nodeData.url)}
                </span>
              </a>
            )}
            {nodeData.source && (
              <p className="text-[9px] text-zinc-600 truncate mt-0.5">
                {nodeData.source}
              </p>
            )}
          </div>
        </div>
      </div>

      <Handle
        type="target"
        position={Position.Top}
        className="!w-2 !h-2 !bg-violet-500 !border-2 !border-[#18181B] !-top-1"
      />
    </motion.div>
  );
}

export const ThankYouNode = memo(ThankYouNodeComponent);
